<template>
  <div class="login-bg">
    <div class="login-container">
      <div class="company-title">南京希丹维特自动门有限公司</div>
      <h2>智能物联自动门管理系统</h2>
      
      <!-- 登录类型选择 -->
      <div class="login-type-tabs">
        <button
          type="button"
          :class="['tab-btn', { active: loginType === 'admin' }]"
          @click="loginType = 'admin'"
        >
          管理员登录
        </button>
        <button
          type="button"
          :class="['tab-btn', { active: loginType === 'location' }]"
          @click="loginType = 'location'"
        >
          客户位置登录
        </button>
      </div>

      <form @submit.prevent="handleLogin">
        <!-- 管理员登录表单 -->
        <template v-if="loginType === 'admin'">
          <div class="form-group">
            <label>用户名：</label>
            <input v-model="username" type="text" required placeholder="请输入用户名" />
          </div>
          <div class="form-group">
            <label>密码：</label>
            <input v-model="password" type="password" required placeholder="请输入密码" />
          </div>
        </template>

        <!-- 客户位置登录表单 -->
        <template v-else>
          <div class="form-group">
            <label>客户位置ID：</label>
            <input v-model="locationId" type="text" required placeholder="请输入客户位置ID" />
          </div>
          <div class="form-group">
            <label>客户密码：</label>
            <input v-model="locationPassword" type="password" required placeholder="请输入客户密码" />
          </div>
        </template>

        <button type="submit" :disabled="loading">{{ loading ? '登录中...' : '登录' }}</button>
        <p v-if="error" class="error">{{ error }}</p>
      </form>
    </div>
    <div class="global-footer">
      <span class="domain">njxdwt.com</span>
      <span class="beian">
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener">苏ICP备2025191804号</a>
      </span>
      <span class="version">v1.0.0</span>
    </div>
  </div>
</template>

<script>
import { authenticateSuperAdmin, authenticateLocationByName } from '@/api/auth';

export default {
  name: 'LoginPage',
  data() {
    return {
      loginType: 'admin', // 'admin' 或 'location'
      username: '',
      password: '',
      locationId: '',
      locationPassword: '',
      error: '',
      loading: false,
    };
  },
  methods: {
    async handleLogin() {
      this.error = '';
      this.loading = true;

      try {
        if (this.loginType === 'admin') {
          // 管理员登录 - 通过后端API认证
          await this.handleAdminLogin();
        } else {
          // 客户位置登录
          await this.handleLocationLogin();
        }
      } catch (err) {
        this.error = err.message || '登录失败，请重试';
      } finally {
        this.loading = false;
      }
    },

    async handleAdminLogin() {
      try {
        const response = await authenticateSuperAdmin({
          username: this.username.trim(),
          password: this.password
        });
        const payload = response.data || {};
        const authData = payload.data || {};

        if (payload.code === 200 && authData.authenticated) {
          this.applyLoginSession(
            {
              ...authData,
              username: authData.username || this.username
            },
            'admin'
          );
          this.$router.replace('/locations');
        } else {
          this.error = payload.message || '超级管理员认证失败';
        }
      } catch (err) {
        console.error('超级管理员登录错误:', err);
        if (err.response && err.response.status === 401) {
          this.error = err.response.data?.message || '用户名或密码错误';
        } else if (err.response) {
          this.error = `登录失败: ${err.response.data.message || '服务器错误'}`;
        } else if (err.request) {
          this.error = '网络错误，无法连接到服务器';
        } else {
          this.error = '登录失败，请重试';
        }
      }
    },

    async handleLocationLogin() {
      try {
        const response = await authenticateLocationByName({
          name: this.locationId.trim(),
          password: this.locationPassword
        });
        const payload = response.data || {};
        const authData = payload.data || {};

        if (payload.code === 200 && authData.authenticated) {
          const normalizedLocationId = authData.locationId || this.locationId.trim();
          const normalizedName = authData.name || authData.locationName || `客户 ${normalizedLocationId}`;
          this.applyLoginSession(
            {
              ...authData,
              locationId: normalizedLocationId,
              name: normalizedName
            },
            'location'
          );
          this.$router.replace(`/state/${normalizedLocationId}`);
        } else {
          this.error = payload.message || '登录失败';
        }
      } catch (err) {
        console.error('客户位置登录失败:', err);
        if (err.response) {
          this.error = `登录失败: ${err.response.data.message || '服务器错误'}`;
        } else if (err.request) {
          this.error = '网络错误，无法连接到服务器';
        } else {
          this.error = '登录失败，请重试';
        }
      }
    },

    /**
     * 兼容不同后端返回结构，提取并转为字符串
     */
    extractToken(data) {
      let token = null;

      if (typeof data === 'string') {
        token = data;
      } else if (data && typeof data === 'object') {
        token = data.token || data.accessToken || data.jwt || data.value || null;
      }

      if (!token) {
        return null;
      }

      return typeof token === 'string' ? token : String(token);
    },

    /**
     * 解析JWT token，提取payload中的信息
     */
    parseJwtPayload(token) {
      try {
        if (!token || typeof token !== 'string') {
          return null;
        }
        // JWT格式：Header.Payload.Signature
        const parts = token.split('.');
        if (parts.length !== 3) {
          console.warn('[JWT解析] Token格式不正确，部分数量:', parts.length);
          return null;
        }
        // 解码payload（base64url）
        const payload = parts[1];
        // base64url解码：将'-'替换为'+'，'_'替换为'/'，然后添加padding
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
        
        // 使用正确的方式解码UTF-8字符（支持中文）
        let decoded;
        try {
          // 方法1：使用decodeURIComponent和escape处理UTF-8
          decoded = decodeURIComponent(escape(atob(padded)));
        } catch (e) {
          // 如果方法1失败，尝试直接使用atob（可能不包含中文）
          try {
            decoded = atob(padded);
          } catch (e2) {
            throw new Error('Base64解码失败: ' + e2.message);
          }
        }
        
        const parsed = JSON.parse(decoded);
        
        // 检查token是否过期
        if (parsed.exp) {
          const now = Math.floor(Date.now() / 1000);
          const expired = now >= parsed.exp;
          if (expired) {
            console.error('[JWT解析] Token已过期:', {
              now,
              exp: parsed.exp,
              expiredAt: new Date(parsed.exp * 1000).toLocaleString(),
              expiredSecondsAgo: now - parsed.exp
            });
          } else {
            const remainingSeconds = parsed.exp - now;
            console.log('[JWT解析] Token有效，剩余时间:', {
              remainingSeconds,
              remainingMinutes: Math.floor(remainingSeconds / 60),
              expiresAt: new Date(parsed.exp * 1000).toLocaleString()
            });
          }
        }
        
        return parsed;
      } catch (error) {
        console.error('[JWT解析] 解析失败:', error, 'Token前缀:', token?.substring(0, 50));
        return null;
      }
    },

    applyLoginSession(payload, fallbackType) {
      const token = this.extractToken(payload);
      const role = (payload.role || fallbackType || '').toUpperCase();
      const userType = role === 'HOSPITAL_USER'
        ? 'location'
        : (role === 'SUPER_ADMIN' ? 'admin' : fallbackType);

      if (!token) {
        throw new Error('未获取到访问令牌，请联系管理员检查登录接口返回');
      }

      localStorage.setItem('authToken', token);
      localStorage.setItem('jwtToken', token);
      
      // 解析JWT payload，提取其中的信息
      const jwtPayload = this.parseJwtPayload(token);
      console.log('[登录] JWT Payload解析结果:', jwtPayload);
      
      // 调试信息：确认JWT已保存
      console.log('[登录] JWT Token已保存:', {
        userType,
        role,
        tokenLength: token.length,
        tokenPrefix: token.substring(0, 20) + '...',
        jwtLocationId: jwtPayload?.locationId,
        payloadLocationId: payload.locationId
      });

      const username = payload.username || payload.name || jwtPayload?.sub || '';
      if (username) {
        localStorage.setItem('username', username);
      } else {
        localStorage.removeItem('username');
      }

      // 优先使用JWT中的role，如果没有则使用payload中的
      // 后端JWT中的role字段如：HOSPITAL_USER, SUPER_ADMIN
      const finalRole = jwtPayload?.role || role || (jwtPayload ? 'HOSPITAL_USER' : null);
      if (finalRole) {
        const roleUpper = finalRole.toUpperCase();
        localStorage.setItem('userRole', roleUpper);
        console.log('[登录] 保存userRole:', {
          fromJWT: jwtPayload?.role,
          fromPayload: role,
          final: roleUpper
        });
      } else {
        console.warn('[登录] 警告：未找到role字段');
        localStorage.removeItem('userRole');
      }

      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userType', userType);

      if (userType === 'location') {
        // 优先从JWT中提取locationId，如果没有则从payload中获取
        const locationId = jwtPayload?.locationId || payload.locationId || '';
        const locationName = jwtPayload?.locationName || payload.name || payload.locationName || '';
        
        console.log('[登录] 保存locationId:', {
          fromJWT: jwtPayload?.locationId,
          fromPayload: payload.locationId,
          final: locationId
        });
        
        if (locationId) {
          localStorage.setItem('locationId', String(locationId));
        } else {
          console.warn('[登录] 警告：未找到locationId，JWT payload:', jwtPayload, 'payload:', payload);
          localStorage.removeItem('locationId');
        }
        
        if (locationName) {
          localStorage.setItem('locationName', locationName);
        } else {
          localStorage.removeItem('locationName');
        }
      } else {
        localStorage.removeItem('locationId');
        localStorage.removeItem('locationName');
      }
    }
  },
};
</script>

<style scoped>
.login-bg {
  min-height: 100vh;
  width: 100vw;
  background: linear-gradient(135deg, #0f2027 0%, #2c5364 100%), url('https://cdn.jsdelivr.net/gh/xidanweite/cdn-tech/bg-tech-lines.svg');
  background-size: cover, cover;
  background-repeat: no-repeat, no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  position: relative;
}

.login-container {
  max-width: 420px;
  margin: 60px auto 30px auto;
  padding: 32px 28px 18px 28px;
  border-radius: 16px;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
  background: rgba(255, 255, 255, 0.10);
  backdrop-filter: blur(8px);
  border: 1.5px solid rgba(0,243,255,0.18);
  position: relative;
  z-index: 2;
}

.company-title {
  text-align: center;
  font-size: 1.25em;
  font-weight: bold;
  color: #00f3ff;
  margin-bottom: 18px;
  letter-spacing: 2px;
  text-shadow: 0 0 8px #00f3ff44;
}

h2 {
  text-align: center;
  color: #fff;
  margin-bottom: 18px;
  letter-spacing: 1px;
  text-shadow: 0 0 8px #00f3ff33;
}

.login-type-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.tab-btn {
  flex: 1;
  padding: 10px;
  background: rgba(255,255,255,0.1);
  color: #00f3ff;
  border: 1.5px solid #00f3ff44;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.95em;
  font-weight: bold;
  letter-spacing: 1px;
  transition: all 0.3s;
}

.tab-btn:hover {
  background: rgba(0,243,255,0.15);
  border-color: #00f3ff88;
}

.tab-btn.active {
  background: linear-gradient(90deg, #00f3ff 0%, #2c5364 100%);
  color: #fff;
  border-color: #00f3ff;
  box-shadow: 0 2px 8px #00f3ff33;
}

.form-group {
  margin-bottom: 18px;
}

label {
  display: block;
  margin-bottom: 6px;
  font-weight: bold;
  color: #00f3ff;
  letter-spacing: 1px;
}

input {
  width: 100%;
  padding: 10px;
  border: 1.5px solid #00f3ff44;
  border-radius: 6px;
  background: rgba(255,255,255,0.15);
  color: #222;
  font-size: 1em;
  outline: none;
  transition: border 0.2s;
}
input:focus {
  border: 1.5px solid #00f3ff;
  background: #fff;
}

button[type="submit"] {
  width: 100%;
  padding: 12px;
  background: linear-gradient(90deg, #00f3ff 0%, #2c5364 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1.1em;
  font-weight: bold;
  letter-spacing: 2px;
  margin-top: 8px;
  box-shadow: 0 2px 8px #00f3ff33;
  transition: background 0.2s;
}

button[type="submit"]:hover:not(:disabled) {
  background: linear-gradient(90deg, #2c5364 0%, #00f3ff 100%);
}

button[type="submit"]:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error {
  color: #ff4757;
  margin-top: 10px;
  text-align: center;
  font-weight: bold;
  letter-spacing: 1px;
}

.global-footer {
  width: 100vw;
  position: fixed;
  left: 0;
  bottom: 0;
  background: rgba(0,0,0,0.18);
  color: #00f3ff;
  text-align: center;
  padding: 10px 0 8px 0;
  font-size: 0.98em;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}
.domain {
  font-weight: bold;
  color: #00f3ff;
  letter-spacing: 1px;
}
.beian a {
  color: #00f3ff;
  text-decoration: underline;
  font-size: 0.97em;
}
.version {
  color: #fff;
  font-size: 0.95em;
  margin-top: 2px;
}
</style>